import type {ReactElement} from 'react';
import {useEffect, useMemo, useRef, useState} from 'react';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import type {CarouselItemData, CarouselProps} from '@Components/carousel/carousel.types';
import {CarouselClickBehaviour} from '@Components/carousel/carousel.types';
import {addGrid, addOrReplaceGridItems, getGridItemForStoredData, toggleGridItemSelection, updateMaxSelectionNumberForGrid} from '@Components/base-grid';
import {getUniqueString} from '@Utils/string.util';
import {AnimatePresence, motion} from 'framer-motion';
import styles from './carousel.module.scss';
import {useAppDispatch} from '@/hooks';

const GAP = 8;
const HEIGHT = 100;
const SELECTION_BADGE_OVERFLOW = 8;
const SCROLL_BUTTON_TOLERANCE = 2;

const dedupeCarouselItemsById = (items: CarouselItemData[]): CarouselItemData[] => {
  const seenIds = new Set<string>();
  return items.filter((item) => {
    if (seenIds.has(item.gridItemData.id)) {
      return false;
    }
    seenIds.add(item.gridItemData.id);
    return true;
  });
};

function Carousel({items, onNext = (): void => {}, onPrev = (): void => {}, height = HEIGHT, ...props}: CarouselProps): ReactElement<any> {
  const carouselItemContainerRef = useRef<HTMLUListElement>(null);
  const gridID = useRef(props.gridID ?? getUniqueString());
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const dispatch = useAppDispatch();
  const dedupedItems = useMemo(() => dedupeCarouselItemsById(items), [items]);
  const paddingTop = props.maxSelectionNumber !== 1 ? SELECTION_BADGE_OVERFLOW : 0;

  const getControlButton = (className: string, iconClass: string, callback: () => void): ReactElement<any> => {
    return (
      <Icon
        className={className}
        isDisabled={false}
        icon={iconClass}
        type={IconType.SHADOW}
        onClick={callback}
        size={props.controlButtonIconSize ?? IconSize.SIZE_ICON_20}
        shape={IconShape.CIRCLE}
        isUsedAsButton
      />
    );
  };

  const scrollToPosition = (left: number): void => {
    carouselItemContainerRef.current?.scrollTo({
      left,
      top: 0,
      behavior: 'smooth',
    });
  };

  const updateScrollButtons = (): void => {
    const container = carouselItemContainerRef.current;
    if (!container) {
      return;
    }
    const maxScroll = container.scrollWidth - container.clientWidth;
    setCanScrollPrev(container.scrollLeft > SCROLL_BUTTON_TOLERANCE);
    setCanScrollNext(container.scrollLeft < maxScroll - SCROLL_BUTTON_TOLERANCE);
  };

  const getItemStartAtOrBefore = (position: number): number => {
    const container = carouselItemContainerRef.current;
    if (!container) {
      return 0;
    }
    let start = 0;
    for (const child of container.children) {
      if (!(child instanceof HTMLElement)) {
        continue;
      }
      if (child.offsetLeft > position + SCROLL_BUTTON_TOLERANCE) {
        break;
      }
      start = child.offsetLeft;
    }
    return start;
  };

  const onNextSlide = (): void => {
    const container = carouselItemContainerRef.current;
    if (!container) {
      return;
    }
    const maxScroll = container.scrollWidth - container.clientWidth;
    let target = getItemStartAtOrBefore(container.scrollLeft + container.clientWidth);
    // An item wider than the viewport would leave the target on the current item; step forward a viewport so the carousel never stalls.
    if (target <= container.scrollLeft + SCROLL_BUTTON_TOLERANCE) {
      target = container.scrollLeft + container.clientWidth;
    }
    scrollToPosition(Math.min(target, maxScroll));
    onNext();
  };

  const onPrevSlide = (): void => {
    const container = carouselItemContainerRef.current;
    if (!container) {
      return;
    }
    let target = getItemStartAtOrBefore(container.scrollLeft - container.clientWidth);
    // Guarantee backward progress when the current item is wider than the viewport.
    if (target >= container.scrollLeft - SCROLL_BUTTON_TOLERANCE) {
      target = container.scrollLeft - container.clientWidth;
    }
    scrollToPosition(Math.max(target, 0));
    onPrev();
  };

  const getGridItemForData = (item: CarouselItemData): ReactElement<any> | null => {
    const naturalWidth = (item.width * height) / item.height;
    const maxWidth = item.hasFixedWidth || props.maxItemAspectRatio === undefined ? naturalWidth : props.maxItemAspectRatio * height;
    const itemWidth = Math.min(naturalWidth, maxWidth);
    const isWidthClamped = itemWidth < naturalWidth;
    return getGridItemForStoredData({
      item: {...item.gridItemData, gridItemHeight: height},
      paddedThumb: false,
      isSmallItem: true,
      gridId: gridID.current,
      gridSelectionGroupId: props.selectionGroupId ?? gridID.current,
      className: `flex-center radius-4 ${styles.carouselItem} ${isWidthClamped ? styles.containedThumbItem : ''} ${item.gridItemData.className ?? ''}`,
      showSelectionNumber: props.maxSelectionNumber !== 1,
      showHoverOverlay: true,
      isDraggable: props.isItemDraggable,
      onItemPointerDown: props.onItemPointerDown,
      onClicked: (): void => {
        if (props.clickBehaviour === CarouselClickBehaviour.SELECT) {
          dispatch(
            toggleGridItemSelection({
              gridItemId: item.gridItemData.id,
              gridSelectionGroupId: props.selectionGroupId ?? gridID.current,
            })
          );
        } else if (props.onSingleItemClick) {
          void props.onSingleItemClick([item.gridItemData]);
        }
        if (props.onCarouselItemClicked) {
          props.onCarouselItemClicked(item.gridItemData.id);
        }
      },

      style: {width: `${itemWidth}px`, height: `${height}px`},
    });
  };

  useEffect(() => {
    updateScrollButtons();
    const container = carouselItemContainerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const resizeObserver = new ResizeObserver((): void => {
      updateScrollButtons();
    });
    resizeObserver.observe(container);
    return (): void => {
      resizeObserver.disconnect();
    };
  }, [dedupedItems]);

  useEffect(() => {
    dispatch(
      updateMaxSelectionNumberForGrid({
        gridSelectionGroupId: props.selectionGroupId ?? gridID.current,
        maxSelectionNumber: props.maxSelectionNumber ?? 0,
      })
    );
  }, [props.maxSelectionNumber]);

  useEffect(() => {
    dispatch(
      addGrid({
        id: gridID.current,
        gridSelectionGroupId: props.selectionGroupId ?? gridID.current,
        maxSelectionNumber: props.maxSelectionNumber,
        items: dedupedItems.map((item) => {
          return item.gridItemData;
        }),
      })
    );
    // addGrid is a no-op for a grid that survived a previous mount, which would leave stale items behind — items
    // fetched this mount (e.g. a freshly generated AI image) must be in the store or selection can't resolve them.
    dispatch(
      addOrReplaceGridItems({
        gridId: gridID.current,
        gridItems: Object.fromEntries(dedupedItems.map((item) => [item.gridItemData.id, item.gridItemData])),
      })
    );
  }, []);

  return (
    <div className={`${styles.carouselContainer} flex-row-justify-center`} style={{height: `${height + paddingTop}px`, marginTop: `${-paddingTop}px`}}>
      <div className={`flex ${styles.carouselItemContainerWrapper} _full-width`}>
        <ul
          onScroll={updateScrollButtons}
          ref={carouselItemContainerRef}
          className={`${styles.carouselItemContainer} spacing-m-0 spacing-p-l-4 spacing-p-r-4 _full-width _full-height`}
          style={{gap: `${GAP}px`, paddingTop: `${paddingTop}px`}}
        >
          {dedupedItems.map(getGridItemForData)}
        </ul>
      </div>

      <AnimatePresence initial={false}>
        {canScrollPrev ? (
          <motion.div
            className={`${styles.carouselControls} ${styles.pushToLeft}`}
            style={{top: `${props.controlButtonTopPositioning ?? paddingTop / 2}px`, left: props.prevBtnLeftPositioning ? `${props.prevBtnLeftPositioning}px` : undefined}}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.2}}
            exit={{opacity: 0}}
          >
            {getControlButton(styles.prevBtn, 'icon-caret-left', onPrevSlide)}
          </motion.div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {canScrollNext ? (
          <motion.div
            className={`${styles.carouselControls} ${styles.pushToRight}`}
            style={{top: `${props.controlButtonTopPositioning ?? paddingTop / 2}px`, right: props.nextBtnRightPositioning ? `${props.nextBtnRightPositioning}px` : undefined}}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.2}}
            exit={{opacity: 0}}
          >
            {getControlButton(styles.nextBtn, 'icon-caret-right', onNextSlide)}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export default Carousel;
